import React from 'react';
import { useAppDispatch } from 'hooks/redux';
import { isEqual } from 'lodash';
import { setElementValues } from 'store/rfiSlice';
import { answerValueTypes, IElementProps } from 'types/RfiTypes';
import { Editor, EditorChangeEvent, EditorTools, EditorUtils } from '@progress/kendo-react-editor';

const {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Indent,
  Outdent,
  OrderedList,
  UnorderedList,
  FontSize,
  FontName,
  FormatBlock,
  Link,
  Unlink,
  InsertImage,
  ViewHtml,
  InsertTable,
  AddRowBefore,
  AddRowAfter,
  AddColumnBefore,
  AddColumnAfter,
  DeleteRow,
  DeleteColumn,
  DeleteTable,
  MergeCells,
  SplitCell,
  Undo,
  Redo,
  Subscript,
  Superscript,
} = EditorTools;

const {
  pasteCleanup,
  sanitize,
  sanitizeClassAttr,
  sanitizeStyleAttr,
  removeAttribute,
  replaceImageSourcesFromRtf,
} = EditorUtils;

const pasteSettings = {
  convertMsLists: true,
  // stripTags: 'span|font'
  attributes: {
    class: sanitizeClassAttr,
    style: sanitizeStyleAttr,
    // keep `width`, `height` and `src` attributes
    width: () => {},
    height: () => {},
    src: () => {},
    // Removes `lang` attribute
    // lang: removeAttribute,

    // removes other (unspecified above) attributes
    "*": removeAttribute,
  },
};

const RichTextEditor = (props: IElementProps) => {
  // Hooks
  const dispatch = useAppDispatch();

  // State
  let defaultValue =
    props.element.answers && props.element.answers[0]?.value
      ? props.element.answers[0]?.value
      : "";

  const [value, setValue] = React.useState(defaultValue);

  // Handlers
  const handleChange = (e: EditorChangeEvent) => {
    // props.element.setValues([
    //   {
    //     value: e.html,
    //     answerValueType: answerValueTypes.RICH_TEXT,
    //   },
    // ]);

    dispatch(
      setElementValues({
        elementId: props.element.elementId,
        values: [
          {
            value: e.html,
            answerValueType: answerValueTypes.RICH_TEXT,
          },
        ],
      })
    );

    setValue(e.html);
    //console.log("value", value);
  };

  // Render
  //if (props.element.elementType === elementTypes.FIELD){
  if (props.readOnly) {
    return (
      <div style={{ marginLeft: "20px" }}>
        <div dangerouslySetInnerHTML={{ __html: value }}></div>
      </div>
    );
  } else
    return (
      <div style={{ marginLeft: "20px" }}>
        <strong>
          {props.element.fieldLabel}{" "}
          {props.overrideRequired === undefined ? (
            props.element.requiredResponse ? (
              <span className="requiredField">(required)</span>
            ) : (
              ""
            )
          ) : props.overrideRequired === true ? (
            <span className="requiredField">(required)</span>
          ) : (
            ""
          )}
        </strong>
        <Editor
          ariaLabel={`${props.element.fieldLabel}`}
          onPasteHtml={(event) => {
            let html = pasteCleanup(sanitize(event.pastedHtml), pasteSettings);

            // If the pasted HTML contains images with sources pointing to the local file system,
            // `replaceImageSourcesFromRtf` will extract the sources from the RTF and place them to images 'src' attribute in base64 format.
            if (event.nativeEvent.clipboardData) {
              html = replaceImageSourcesFromRtf(
                html,
                event.nativeEvent.clipboardData
              );
            }
            return html;
          }}
          tools={[
            [Bold, Italic, Underline, Strikethrough],
            [Subscript, Superscript],
            [AlignLeft, AlignCenter, AlignRight, AlignJustify],
            [Indent, Outdent],
            [OrderedList, UnorderedList],
            FontSize,
            FontName,
            FormatBlock,
            [Undo, Redo],
            [Link, Unlink, InsertImage],
            [InsertTable],
            [AddRowBefore, AddRowAfter, AddColumnBefore, AddColumnAfter],
            [DeleteRow, DeleteColumn, DeleteTable],
            [MergeCells, SplitCell],
          ]}
          value={value}
          onChange={handleChange}
          contentStyle={{ height: 430 }}
        />
      </div>
    );
};

export default RichTextEditor;

const areEqual = (prevProps: IElementProps, nextProps: IElementProps) => {
  return (
    !prevProps.rerender &&
    isEqual(prevProps.element, nextProps.element) &&
    isEqual(prevProps.designMode, nextProps.designMode) &&
    isEqual(prevProps.readOnly, nextProps.readOnly)
  );
};

const RichTextElementMemo = React.memo(RichTextEditor, areEqual);
